import React from 'react';
import './PrivacyPolicy.css';
import PageHero from '../components/PageHero';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-root">
      <PageHero
        title="Privacy Policy"
        subtitle="How The Video Company (TVC) collects, uses, and protects your information"
        align="left"
      />

      <section className="privacy-policy-container">
        <div className="privacy-header">
          <p className="last-updated">Last updated: December 11, 2025</p>
          <p className="privacy-intro">
            This Privacy Policy outlines how The Video Company – TVC collects, uses, stores, and protects personal and project information shared by clients, job applicants, and website visitors. TVC is committed to maintaining the confidentiality and security of all data.
          </p>
        </div>

        {/* Section 1 */}
        <div className="privacy-section">
          <h2>1. Information We Collect</h2>
          <ul className="privacy-list">
            <li><strong>Client Information:</strong> Name, email, phone number, company name, project requirements, budget, and location details.</li>
            <li><strong>Payment Information:</strong> Bank details, UPI IDs, payment gateway information (processed securely through authorized payment providers).</li>
            <li><strong>Project Data:</strong> Scripts, brand guidelines, footage, references, and communications related to video production.</li>
            <li><strong>Applicant Information:</strong> Resume, contact details, portfolio links, and interview records from job applicants.</li>
            <li><strong>Website Visitor Data:</strong> IP address, browser type, device information, and pages visited (via analytics).</li>
          </ul>
        </div>

        {/* Section 2 */}
        <div className="privacy-section">
          <h2>2. How We Use Your Information</h2>
          <ul className="privacy-list">
            <li>To deliver video production services, communicate project details, and manage timelines.</li>
            <li>To process payments and send invoices or receipts.</li>
            <li>To assess job applications and contact shortlisted candidates.</li>
            <li>To improve website functionality and user experience through analytics.</li>
            <li>To send marketing emails or newsletters (only to opted-in users).</li>
            <li>To comply with legal and regulatory requirements.</li>
            <li>To resolve disputes and ensure project satisfaction.</li>
          </ul>
        </div>

        {/* Section 3 */}
        <div className="privacy-section">
          <h2>3. Data Security & Storage</h2>
          <ul className="privacy-list">
            <li>All client project files are stored securely with encryption protocols.</li>
            <li>Payment information is never stored by TVC; it is processed through secure, PCI-DSS compliant payment gateways.</li>
            <li>Project files are backed up with <strong>45–60 days cloud storage</strong> and <strong>15 months offline archival.</strong></li>
            <li>Access to sensitive data is restricted to authorized TVC team members only.</li>
            <li>TVC uses industry-standard security measures to protect against unauthorized access, loss, or theft.</li>
          </ul>
        </div>

        {/* Section 4 */}
        <div className="privacy-section">
          <h2>4. Data Retention</h2>
          <ul className="privacy-list">
            <li>Project files and client communication are retained for <strong>15 months</strong> after project completion for reference and support purposes.</li>
            <li>Payment records are maintained for <strong>7 years</strong> for compliance with Indian taxation laws.</li>
            <li>Job applicant information is retained for <strong>12 months</strong> for future recruitment purposes.</li>
            <li>Website analytics data is retained for <strong>2 years</strong> for performance analysis.</li>
            <li>Clients may request data deletion at any time, subject to legal and contractual obligations.</li>
          </ul>
        </div>

        {/* Section 5 */}
        <div className="privacy-section">
          <h2>5. Sharing of Information</h2>
          <p><strong>TVC will NOT share your personal information with third parties, except:</strong></p>
          <ul className="privacy-list">
            <li>With authorized payment processors (for secure payment handling).</li>
            <li>With cloud storage providers (for secure file backup and archival).</li>
            <li>With legal authorities, if required by law or court order.</li>
            <li>With team members and contractors directly involved in your project (under confidentiality agreements).</li>
            <li>As part of project portfolio content (final videos and visuals only, with client consent where applicable).</li>
          </ul>
        </div>

        {/* Section 6 */}
        <div className="privacy-section">
          <h2>6. Your Rights</h2>
          <p><strong>You have the right to:</strong></p>
          <ul className="privacy-list">
            <li>Access your personal data held by TVC.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of your data (subject to legal retention requirements).</li>
            <li>Opt-out of marketing communications at any time.</li>
            <li>Withdraw consent for data processing (may affect service delivery).</li>
            <li>Request a copy of your data in portable format.</li>
          </ul>
          <p className="important-note"><strong>Note:</strong> Data requests must be submitted in writing to privacy@thevideocompany.in with proof of identity. We will respond within 30 days.</p>
        </div>

        {/* Section 7 */}
        <div className="privacy-section">
          <h2>7. Cookies & Analytics</h2>
          <ul className="privacy-list">
            <li>TVC website uses cookies to enhance user experience and track website analytics.</li>
            <li>We use Google Analytics to understand visitor behavior and improve our services.</li>
            <li>You can disable cookies through your browser settings; however, this may limit website functionality.</li>
            <li>No personally identifiable information is collected through cookies without explicit consent.</li>
          </ul>
        </div>

        {/* Section 8 */}
        <div className="privacy-section">
          <h2>8. Third-Party Links</h2>
          <p>
            TVC website may contain links to third-party websites or social media platforms. <strong>We are not responsible for the privacy practices of external websites.</strong> Please review their privacy policies before sharing information.
          </p>
        </div>

        {/* Section 9 */}
        <div className="privacy-section">
          <h2>9. International Data Transfers</h2>
          <p>
            TVC operates from India and primarily stores data within India. If data is transferred internationally for cloud storage or other purposes, it will be protected with appropriate safeguards and compliance with applicable data protection laws.
          </p>
        </div>

        {/* Section 10 */}
        <div className="privacy-section">
          <h2>10. Children's Privacy</h2>
          <p>
            TVC services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware of such data, it will be deleted immediately.
          </p>
        </div>

        {/* Section 11 */}
        <div className="privacy-section">
          <h2>11. Changes to Privacy Policy</h2>
          <p>
            TVC reserves the right to update this Privacy Policy at any time. Material changes will be communicated via email to registered clients. Continued use of TVC services implies acceptance of the updated policy.
          </p>
        </div>

        {/* Section 12 */}
        <div className="privacy-section">
          <h2>12. Compliance with Laws</h2>
          <ul className="privacy-list">
            <li>TVC complies with the <strong>Information Technology Act, 2000</strong> and its Rules.</li>
            <li>We adhere to applicable data protection and privacy regulations in India.</li>
            <li>For disputes, TVC will cooperate with regulatory authorities and courts of India.</li>
          </ul>
        </div>

        {/* Contact Section */}
        <div className="contact-section">
          <h2>Contact Us</h2>
          <p>For privacy concerns, data requests, or feedback, please contact:</p>
          <ul className="contact-list">
            <li><strong>Email:</strong> connect@thevideocompany.in</li>
            <li><strong>Phone:</strong> +91 7411611528</li>
            <li><strong>Address:</strong> The Video Company, Bangalore, India</li>
          </ul>
          <p className="acceptance-text">
            By using TVC services, you acknowledge that you have read and understood this Privacy Policy and consent to the collection, use, and storage of your information as described herein.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
